import { useCallback, useMemo, useState } from 'react';
import { ReportPageLinks } from '../../components/report/ReportPageLinks';
import { useCrmPageNotifications } from '../../contexts/CrmPageNotificationsContext';
import {
  enrichStorageBillingRows,
  exportStorageBillingExcel,
  fetchStorageBillingReport,
  type StorageBillingReport,
} from '../../lib/storageBillingReport';
import { formatYmdAsCrmDate } from '../../utils/dateTime';

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDdMmYyyy(iso: string | null | undefined): string {
  if (!iso) return '—';
  const raw = iso.slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatYmdAsCrmDate(raw);
  return iso;
}

function formatQty(n: number | null | undefined): string {
  if (n == null) return '';
  return n.toLocaleString('en-SG', { maximumFractionDigits: 0 });
}

type StatusFilter = 'all' | 'overdue' | 'within';

export function StorageBillingReportPage() {
  const { push } = useCrmPageNotifications();
  const [asOf, setAsOf] = useState(todayIso);
  const [freeMonths, setFreeMonths] = useState(3);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [report, setReport] = useState<StorageBillingReport | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchStorageBillingReport({ asOf, freeMonths });
      setReport(data);
      push({
        tone: 'ok',
        title: 'Storage billing loaded',
        lines: [`${data.row_count.toLocaleString()} GRN line(s) · as of ${formatDdMmYyyy(data.as_of)}`],
      });
    } catch (err) {
      setReport(null);
      push({
        tone: 'warn',
        title: 'Storage billing failed',
        lines: [err instanceof Error ? err.message : String(err)],
      });
    } finally {
      setLoading(false);
    }
  }, [asOf, freeMonths, push]);

  const enrichedRows = useMemo(() => enrichStorageBillingRows(report?.rows ?? []), [report]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return enrichedRows.filter(({ row, display }) => {
      if (statusFilter === 'overdue' && row.status !== 'Overdue') return false;
      if (statusFilter === 'within' && row.status !== 'Within free Storage') return false;
      if (!q) return true;
      const hay = [
        display.customer,
        display.salesperson,
        display.item,
        display.description,
        row.grn_no,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [enrichedRows, search, statusFilter]);

  const stats = useMemo(() => {
    const rows = report?.rows ?? [];
    const headers = rows.filter((r) => r.is_header);
    const overdue = rows.filter((r) => r.status === 'Overdue').length;
    const within = rows.filter((r) => r.status === 'Within free Storage').length;
    return {
      items: headers.length,
      lines: rows.length,
      overdue,
      within,
    };
  }, [report]);

  const onExport = useCallback(async () => {
    if (!report) return;
    setExporting(true);
    try {
      const summaryEntries =
        statusFilter === 'all' && !search.trim() ? undefined : filteredRows;
      await exportStorageBillingExcel(report, { summaryEntries });
      push({ tone: 'ok', title: 'Excel exported' });
    } catch (err) {
      push({
        tone: 'warn',
        title: 'Export failed',
        lines: [err instanceof Error ? err.message : String(err)],
      });
    } finally {
      setExporting(false);
    }
  }, [filteredRows, push, report, search, statusFilter]);

  return (
    <div className="report-storage-page">
      <header className="page-header">
        <div className="page-header__top">
          <div className="report-storage-page__title-block">
            <h1 className="page-title">Storage Billing</h1>
            <p className="page-lead">
              ICRE customers · FG-XOC / FG-PD / FG-R / FG-OBR / FG-OBX · combine -PD/-B · Purchase ILE GRN qty
              (base UOM) · newest→oldest · free storage {freeMonths} months
            </p>
          </div>
          <div className="page-header__actions report-storage-page__actions">
            <ReportPageLinks />
            <button type="button" className="btn-secondary btn-sm" disabled={!report || exporting} onClick={() => void onExport()}>
              {exporting ? 'Exporting…' : 'Export Excel'}
            </button>
            <button type="button" className="btn-primary btn-sm" disabled={loading} onClick={() => void load()}>
              {loading ? 'Loading…' : report ? 'Refresh' : 'Generate'}
            </button>
          </div>
        </div>
      </header>

      <div className="report-storage-page__filters">
        <label className="field">
          <span className="field-label">As of</span>
          <input
            type="date"
            className="field-input"
            value={asOf}
            onChange={(e) => setAsOf(e.target.value)}
          />
        </label>
        <label className="field">
          <span className="field-label">Free months</span>
          <input
            type="number"
            className="field-input"
            min={0}
            max={36}
            value={freeMonths}
            onChange={(e) => setFreeMonths(Number(e.target.value) || 0)}
          />
        </label>
        <label className="field">
          <span className="field-label">Status</span>
          <select
            className="field-input"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          >
            <option value="all">All</option>
            <option value="overdue">Overdue</option>
            <option value="within">Within free Storage</option>
          </select>
        </label>
        <label className="field report-storage-page__search">
          <span className="field-label">Search</span>
          <input
            type="search"
            className="field-input"
            placeholder="Customer / salesperson / item / GRN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
      </div>

      {report ? (
        <div className="report-storage-page__kpis">
          <div className="kpi-card">
            <div className="kpi-label">Items</div>
            <div className="kpi-value">{stats.items.toLocaleString()}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">GRN lines</div>
            <div className="kpi-value">{stats.lines.toLocaleString()}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Overdue lines</div>
            <div className="kpi-value">{stats.overdue.toLocaleString()}</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-label">Within free</div>
            <div className="kpi-value">{stats.within.toLocaleString()}</div>
          </div>
        </div>
      ) : null}

      {!report && !loading ? (
        <p className="page-lead">Choose as-of date, then Generate.</p>
      ) : null}

      {report ? (
        <div className="report-storage-table-wrap">
          <table className="report-storage-table">
            <thead>
              <tr>
                <th>Customer Name</th>
                <th>Salesperson</th>
                <th>Item No.</th>
                <th>Description</th>
                <th>Inventory</th>
                <th>UOM</th>
                <th>Status</th>
                <th>Day left</th>
                <th>GRN Date</th>
                <th>GRN Qty</th>
                <th>Document No.</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={11} className="report-storage-table__empty">
                    No rows match the current filters.
                  </td>
                </tr>
              ) : (
                filteredRows.map(({ row, display }, idx) => (
                  <tr
                    key={`${display.item}-${row.grn_no ?? ''}-${row.grn_date ?? ''}-${idx}`}
                    className={row.status === 'Overdue' ? 'report-storage-table__row--overdue' : undefined}
                  >
                    <td>{display.customer}</td>
                    <td className="report-storage-table__mono">{display.salesperson}</td>
                    <td className="report-storage-table__mono">{display.item}</td>
                    <td>{display.description}</td>
                    <td className="report-storage-table__num">{formatQty(display.inventory)}</td>
                    <td>{display.uom}</td>
                    <td>
                      {row.status ? (
                        <span
                          className={
                            row.status === 'Overdue'
                              ? 'report-storage-badge report-storage-badge--overdue'
                              : 'report-storage-badge report-storage-badge--ok'
                          }
                        >
                          {row.status}
                        </span>
                      ) : null}
                    </td>
                    <td className="report-storage-table__num">{row.day_left ?? ''}</td>
                    <td>{formatDdMmYyyy(row.grn_date)}</td>
                    <td className="report-storage-table__num">{formatQty(row.grn_quantity)}</td>
                    <td className="report-storage-table__mono">{row.grn_no ?? ''}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
